import { Icon } from '@smartech/ui';
import { useEffect, useState } from 'react';

export interface ToastProps {
  id: string;
  title?: string;
  message?: string;
  variant?: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
  action?: React.ReactNode;
  onClose: (id: string) => void;
}

const variantStyles = {
  success: {
    // textColor: 'text-success-800',
    iconColor: 'text-success-600',
    icon: 'check-circle' as const,
  },
  error: {
    // textColor: 'text-error-800',
    iconColor: 'text-error-600',
    icon: 'x-circle' as const,
  },
  warning: {
    // textColor: 'text-warning-800',
    iconColor: 'text-warning-600',
    icon: 'alert-triangle' as const,
  },
  info: {
    // textColor: 'text-info-800',
    iconColor: 'text-warning-600',
    icon: 'info-circle' as const,
  },
};

export default function Toast({
  id,
  title,
  message,
  variant = 'success',
  duration = 3000,
  action,
  onClose,
}: ToastProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  const styles = variantStyles[variant];

  // Determine border color based on variant
  const borderColor =
    variant === 'success'
      ? 'border-success-primary'
      : variant === 'error'
        ? 'border-fg-error-primary'
        : variant === 'warning'
          ? 'border-warning-800'
          : variant === 'info'
            ? 'border-warning-700'
            : 'border-border-primary';

  useEffect(() => {
    // Trigger entrance animation
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose(id);
    }, 300); // Animation duration
  };

  return (
    <div
      className={`pointer-events-auto flex max-w-[95vw] items-start gap-3 rounded-lg border border-border-primary bg-white px-3xl pb-4xl pt-5xl shadow-lg backdrop-blur-sm transition-all duration-300 ease-in-out lg:w-[400px] ${isVisible && !isExiting ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'} `}
    >
      <div className="relative">
        <Icon
          name={styles.icon}
          className={`text-xl ${styles.iconColor} absolute left-[-18px] top-[2px] z-10`}
        />
        <div
          className={`absolute inset-0 right-[-5px] top-[-5px] h-[30px] w-[30px] rounded-full border-2 opacity-30 ${borderColor}`}
        ></div>
        <div
          className={`absolute inset-0 right-[-10px] top-[-10px] h-9xl w-9xl rounded-full border-2 opacity-10 ${borderColor}`}
        ></div>
      </div>

      <div className="mr-6xl min-w-0 flex-1 gap-xs">
        {title && <h4 className={`mb-1 text-sm font-semibold text-fg-primary`}>{title}</h4>}
        {message && <p className={`text-sm text-fg-secondary`}>{message}</p>}
        {action && <div className="mt-2">{action}</div>}
      </div>

      <button
        onClick={handleClose}
        className="mr-auto mt-[-5px] flex rounded p-1 text-fg-quaternary transition-colors hover:bg-gray-100"
      >
        <Icon name="x-close" className="text-xl text-fg-quaternary" />
      </button>
    </div>
  );
}
